(function () {
  const STORAGE_KEY = "pieceworkCart";
  // Set instead of clearing the cart the moment checkout starts — the
  // customer hasn't paid yet at that point, just been handed the invoice
  // URL, and hitting back/the header logo from there used to come back to
  // an empty cart for something never bought. Cleared for real only once
  // checkPendingCheckout() confirms (via /api/checkout-status) that this
  // draft order was actually paid.
  const PENDING_CHECKOUT_KEY = "pieceworkPendingCheckout";
  // Stops re-checking (and re-hitting the backend) a draft order the
  // customer plainly never came back to pay — doesn't touch their cart
  // either way, just gives up asking Shopify about this one.
  const PENDING_CHECKOUT_MAX_AGE_MS = 3 * 24 * 60 * 60 * 1000;
  const strings = (window.PieceworkStrings && window.PieceworkStrings.cart) || {};

  const modalStrings = (window.PieceworkStrings && window.PieceworkStrings.modal) || {};
  const productStrings = (window.PieceworkStrings && window.PieceworkStrings.product) || {};

  function readCart() {
    try {
      const raw = localStorage.getItem(STORAGE_KEY);
      const items = raw ? JSON.parse(raw) : [];
      return Array.isArray(items) ? items : [];
    } catch (err) {
      return [];
    }
  }

  function itemKey(item) {
    return item.type === "preview" ? `preview:${item.previewId}:${item.pieces}` : `product:${item.variantId}`;
  }

  function formatMoney(amount) {
    return `$${Number(amount).toFixed(2)}`;
  }

  // item.title (and the preview thumbnail URL) can carry AI-generated text
  // built from a customer's own search phrase (moderated for content, not
  // constrained in literal characters) — same escaping ai-search.js/
  // hot-now.js use before this reaches an innerHTML template.
  function escapeHtml(value) {
    return String(value ?? "").replace(/[&<>"']/g, (ch) => (
      { "&": "&amp;", "<": "&lt;", ">": "&gt;", '"': "&quot;", "'": "&#39;" }[ch]
    ));
  }

  function getCount(items) {
    return items.reduce((sum, item) => sum + item.quantity, 0);
  }

  function getSubtotal(items) {
    return items.reduce((sum, item) => sum + item.price * item.quantity, 0);
  }

  // Mirrors src/shipping.js — unconditional free shipping (2026-07-16, see
  // docs/decisions-log.md). Kept as its own function rather than inlining
  // 0 at each call site so a future revisit is a one-line change back.
  function getShippingFee(items) {
    return 0;
  }

  // ---------- Meta Pixel: browser-side mid-funnel events ----------
  // window.fbq is initialised in layout/theme.liquid (base library only, no
  // PageView). We fire ONLY the events the Shopify Meta app pixel cannot see,
  // because this cart and checkout are a custom localStorage drawer plus
  // Draft Orders flow, not Shopify's native cart. PageView/ViewContent are
  // the app pixel's job; Purchase is sent server-side from the order webhook
  // (src/metaCapi.js). Wrapped and guarded so tracking can never break the
  // cart if fbevents.js is blocked (ad blockers) or slow to load.
  const PIXEL_CURRENCY =
    (window.Shopify && window.Shopify.currency && window.Shopify.currency.active) || "USD";
  function trackPixel(eventName, data) {
    if (typeof window.fbq !== "function") return;
    try {
      window.fbq("track", eventName, data);
    } catch (err) {
      /* tracking must never interrupt the cart */
    }
  }

  function renderBadge(items) {
    const count = getCount(items);
    document.querySelectorAll("[data-piecework-cart-count]").forEach((el) => {
      el.textContent = count;
      el.hidden = count === 0;
    });
  }

  function writeCart(items) {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(items));
    renderBadge(items);
    if (drawer) renderDrawerContents(items);
  }

  // Re-triggers a CSS animation by forcing a reflow between class removal
  // and re-add — without this, adding two items back-to-back wouldn't
  // restart the animation the second time (the class would already be
  // present, so re-adding it is a no-op as far as the browser is
  // concerned).
  function pulse(el, className) {
    if (!el) return;
    el.classList.remove(className);
    void el.offsetWidth;
    el.classList.add(className);
  }

  // Called once per successful add (from addPreview/addProduct below) so
  // every entry point — quick-add buttons, the search preview modal, the
  // product page — gets the same header feedback without each call site
  // wiring it up separately.
  function pulseHeaderCart() {
    document.querySelectorAll(".site-header__cart").forEach((el) => pulse(el, "is-bouncing"));
    document.querySelectorAll(".cart-count").forEach((el) => pulse(el, "is-popping"));
  }

  // The drawer's z-index sits above the header, so it visually covers the
  // cart icon almost as soon as it starts sliding in — opening it in the
  // same tick as pulseHeaderCart() left no time to actually see the
  // bounce before it disappeared underneath. This delay just lets the
  // bounce register first.
  const DRAWER_OPEN_DELAY_MS = 250;
  function pulseThenOpenDrawer() {
    pulseHeaderCart();
    setTimeout(openDrawer, DRAWER_OPEN_DELAY_MS);
  }

  // Exposed so callers that have their own reference to the exact button
  // that was clicked (the modal's "Add to cart", the product page's add
  // button, a card's quick-add button) can flash it directly — this
  // module doesn't track which specific button triggered an add.
  //
  // `textEl` is optional and defaults to `button` itself — the product
  // page's add button keeps its label in a child <span
  // data-add-to-cart-text> (product-form.js's updateForVariant() depends
  // on that span persisting across variant switches), so overwriting
  // button.textContent directly there would destroy it. Callers whose
  // button text IS the button's own textContent (quick-add cards, the
  // search modal) can omit this.
  function flashAdded(button, addedLabel, textEl) {
    if (!button) return;
    const target = textEl || button;
    const label = addedLabel || "Added ✓";
    const originalText = target.textContent;
    const originalDisabled = button.disabled;
    button.classList.add("is-added");
    target.textContent = label;
    button.disabled = true;
    setTimeout(() => {
      button.classList.remove("is-added");
      target.textContent = originalText;
      button.disabled = originalDisabled;
    }, 900);
  }

  function addPreview(entry) {
    const items = readCart();
    const key = `preview:${entry.previewId}:${entry.pieces}`;
    const existing = items.find((item) => itemKey(item) === key);
    if (existing) {
      existing.quantity += 1;
    } else {
      items.push({ type: "preview", quantity: 1, ...entry });
    }
    writeCart(items);
    pulseThenOpenDrawer();
    trackPixel("AddToCart", {
      content_type: "product",
      content_ids: [`preview:${entry.previewId}`],
      content_name: entry.title,
      value: Number(entry.price) || 0,
      currency: PIXEL_CURRENCY,
    });
  }

  function addProduct(entry) {
    const items = readCart();
    const key = `product:${entry.variantId}`;
    const existing = items.find((item) => itemKey(item) === key);
    if (existing) {
      existing.quantity += 1;
    } else {
      items.push({ type: "product", quantity: 1, ...entry });
    }
    writeCart(items);
    pulseThenOpenDrawer();
    trackPixel("AddToCart", {
      content_type: "product",
      content_ids: [String(entry.variantId)],
      content_name: entry.title,
      value: Number(entry.price) || 0,
      currency: PIXEL_CURRENCY,
    });
  }

  function removeByKey(key) {
    const items = readCart();
    const index = items.findIndex((item) => itemKey(item) === key);
    if (index === -1) return;
    items.splice(index, 1);
    writeCart(items);
  }

  // Min 1 / max 99 (owner-confirmed 2026-07-14) — floor-and-disable at
  // both ends via the stepper's own disabled state, clamped again here
  // defensively. Unlike the old <input type="number">, there's no more
  // "type 0 to remove" path: the stepper's "−" disables at quantity 1
  // rather than deleting the row (see docs/frontend-group-a-spec.md
  // Task 3) — Remove remains the one deliberate way to delete a line item.
  // Keyed rather than index-based (same reason as removeByKey/
  // commitSizeChange): an index captured at click time can go stale if
  // another row's async size-swap merge (commitSizeChange's own
  // REMOVE_TRANSITION_MS-delayed splice) lands first and shifts array
  // positions, which would otherwise silently change the wrong item's
  // quantity.
  function changeQuantity(key, quantity) {
    const items = readCart();
    const index = items.findIndex((item) => itemKey(item) === key);
    if (index === -1) return;
    items[index].quantity = Math.min(99, Math.max(1, quantity));
    writeCart(items);
  }

  let drawer;
  // Set right before a quantity change triggers a re-render, so
  // renderDrawerContents() knows which row to pulse — cleared immediately
  // after so it doesn't linger and re-pulse on unrelated re-renders (e.g.
  // a second item added while the drawer's open).
  let pulseRowIndex = null;
  const REMOVE_TRANSITION_MS = 200;

  // Task 2 (cart size swap): which itemKey currently has its inline
  // size-swap panel open, tracked module-level (mirrors pulseRowIndex)
  // since renderDrawerContents() fully rebuilds [data-cart-items] on
  // every change — a quantity edit or Remove on an unrelated row would
  // otherwise silently wipe an open panel's expanded state.
  let openSizePanelKey = null;

  // Product line items' variant lists, fetched lazily from
  // `${productUrl}.json` the first time a row's size panel opens, cached
  // here (module-scoped, not on the DOM node — an object doesn't fit in
  // a dataset) so reopening the panel later in the same drawer session
  // doesn't refetch. Keyed by itemKey per docs/frontend-group-a-spec.md
  // Task 2 — a swap changes the key, so a swapped row's next open
  // refetches once more, which is a fine tradeoff for the simplicity.
  const productVariantsCache = new Map();

  function findRowByKey(itemsEl, key) {
    return Array.from(itemsEl.querySelectorAll(".piecework-cart-item")).find((row) => row.dataset.cartItemKey === key);
  }

  // JS-measured max-height transition (not the CSS-only grid-template-rows
  // 0fr->1fr trick — already tried and dropped for the FAQ accordion, see
  // docs/frontend-design-plan.md Track 2). Same measurement approach as
  // faq-accordion.js: scrollHeight reports the panel's full unclipped
  // content height regardless of the max-height currently clipping it, so
  // no extra "free the constraint first" step is needed the way the FAQ
  // accordion's `height: auto` trick requires.
  function expandToFit(panel) {
    if (!panel) return;
    if (panel.hidden) {
      panel.hidden = false;
      panel.style.maxHeight = "0px";
      // Force a reflow so the browser paints the 0px frame first — same
      // fix used everywhere else in this codebase a lazily-shown element
      // gets an immediate style flip (cart drawer open, modals, etc.).
      void panel.offsetHeight;
    }
    panel.style.maxHeight = `${panel.scrollHeight}px`;
  }

  function collapsePanel(panel) {
    if (!panel || panel.hidden) return;
    panel.style.maxHeight = "0px";
  }

  // Re-expands a still-open row's panel after a full re-render, instantly
  // (no animation) — the shopper never closed it, the list just got
  // rebuilt out from under them (e.g. a quantity change on another row).
  function reopenPanelAfterRender(item, key, row) {
    const panel = row.querySelector("[data-cart-size-panel]");
    const panelInner = row.querySelector("[data-cart-size-panel-inner]");
    if (!panel || !panelInner) return;
    panel.style.transition = "none";
    populateSizePanel(item, panelInner, key, row);
    void panel.offsetHeight;
    panel.style.transition = "";
  }

  function populateSizePanel(item, panelInner, key, row) {
    if (item.type === "preview") {
      renderPreviewSizePills(item, panelInner, key, row);
    } else {
      renderProductSizePanel(item, panelInner, key, row);
    }
  }

  function renderPreviewSizePills(item, panelInner, key, row) {
    panelInner.innerHTML = "";
    (item.sizes || []).forEach((size) => {
      const pill = document.createElement("button");
      pill.type = "button";
      pill.className = "size-pill" + (size.pieces === item.pieces ? " is-selected" : "");
      pill.textContent = `${size.pieces} pc`;
      pill.addEventListener("click", () => selectPreviewSize(item, size, key, row));
      panelInner.appendChild(pill);
    });
    expandToFit(row.querySelector("[data-cart-size-panel]"));
  }

  function renderProductSizePanel(item, panelInner, key, row) {
    const panel = row.querySelector("[data-cart-size-panel]");
    if (productVariantsCache.has(key)) {
      renderProductPills(productVariantsCache.get(key), item, panelInner, key, row);
      return;
    }
    // Loading state: panel already expanded with a small inline spinner
    // in place of pills, rather than nothing shown until the fetch lands.
    panelInner.innerHTML = '<span class="piecework-cart-item__size-panel-spinner" aria-hidden="true"></span>';
    expandToFit(panel);
    fetch(`${item.productUrl}.json`)
      .then((response) => {
        if (!response.ok) throw new Error("Couldn't load sizes");
        return response.json();
      })
      .then((data) => {
        const variants = (data.product && data.product.variants) || [];
        productVariantsCache.set(key, variants);
        // The panel may have since been closed (or the row removed) while
        // this fetch was in flight — only render into it if it's still
        // the one currently open for this row.
        if (openSizePanelKey !== key) return;
        renderProductPills(variants, item, panelInner, key, row);
      })
      .catch(() => {
        if (openSizePanelKey !== key) return;
        panelInner.innerHTML = `<p class="piecework-cart-item__size-panel-error" data-cart-size-panel-error>${
          strings.sizeLoadError || "Couldn't load sizes — try again"
        }</p>`;
        // Clicking the error text itself retries, without requiring the
        // panel to be closed and reopened first — whichever's less code,
        // per the spec, and this is it.
        const errorEl = panelInner.querySelector("[data-cart-size-panel-error]");
        if (errorEl) {
          errorEl.addEventListener("click", () => renderProductSizePanel(item, panelInner, key, row));
        }
        expandToFit(panel);
      });
  }

  function renderProductPills(variants, item, panelInner, key, row) {
    panelInner.innerHTML = "";
    variants.forEach((variant) => {
      const pill = document.createElement("button");
      pill.type = "button";
      const isSelected = String(variant.id) === String(item.variantId);
      pill.className = "size-pill" + (isSelected ? " is-selected" : "");
      // Shopify's public `${productUrl}.json` endpoint omits `available`
      // entirely for variants with untracked inventory (this store's
      // default) rather than reporting `true` — only disable on an
      // explicit `false`, so an absent field defaults to addable.
      if (variant.available === false) pill.disabled = true;
      const pieces = parseInt(variant.option1, 10);
      pill.textContent = pieces ? `${pieces} pc` : variant.option1;
      pill.addEventListener("click", () => {
        if (variant.available !== false) selectProductSize(item, variant, key, row);
      });
      panelInner.appendChild(pill);
    });
    expandToFit(row.querySelector("[data-cart-size-panel]"));
  }

  function selectPreviewSize(item, size, key, row) {
    if (size.pieces === item.pieces) {
      // No-op change — still closes the panel, no re-render/pulse.
      collapsePanel(row.querySelector("[data-cart-size-panel]"));
      openSizePanelKey = null;
      return;
    }
    const updated = {
      pieces: size.pieces,
      price: size.price,
      title: `${item.baseTitle || item.title} — ${size.pieces} pc`,
    };
    commitSizeChange(key, updated, row);
  }

  function selectProductSize(item, variant, key, row) {
    if (String(variant.id) === String(item.variantId)) {
      collapsePanel(row.querySelector("[data-cart-size-panel]"));
      openSizePanelKey = null;
      return;
    }
    const updated = {
      // NOT variant.price / 100 — that convention applies to
      // product-form.js's variant.price, which comes from a Liquid
      // `{{ product | json }}` embed (cents, an integer). This function's
      // `variant` comes from a fetch of Shopify's public
      // `${productUrl}.json` REST endpoint instead, a different source
      // that serializes price as a plain dollar string (e.g. "27.99") —
      // dividing that by 100 silently produced a price two orders of
      // magnitude too small.
      variantId: variant.id,
      price: Number(variant.price),
      pieces: parseInt(variant.option1, 10),
    };
    commitSizeChange(key, updated, row);
  }

  // Shared by both the preview and product swap paths. Recomputes the
  // item's key from `updatedFields` and either updates this row in place
  // or — if another existing line item already has that exact key —
  // merges into it (owner-approved default, see
  // docs/frontend-group-a-spec.md Task 2 "Merge vs. block": consistent
  // with addPreview()/addProduct()'s existing silent-merge-on-same-key
  // behavior). Looks the row up by key at commit time (not a captured
  // index) — same reason Remove does this: an index captured at click
  // time can go stale if another row's async fetch/removal lands first.
  function commitSizeChange(oldKey, updatedFields, row) {
    openSizePanelKey = null;
    collapsePanel(row.querySelector("[data-cart-size-panel]"));

    const merged = { ...readCart().find((it) => itemKey(it) === oldKey), ...updatedFields };
    const newKey = itemKey(merged);
    const collides = newKey !== oldKey && readCart().some((it) => itemKey(it) === newKey);
    const prefersReducedMotion = window.matchMedia("(prefers-reduced-motion: reduce)").matches;

    function apply() {
      const items = readCart();
      const currentIndex = items.findIndex((it) => itemKey(it) === oldKey);
      if (currentIndex === -1) return;
      const collisionIndex = items.findIndex((it, i) => i !== currentIndex && itemKey(it) === newKey);
      if (collisionIndex !== -1) {
        items[collisionIndex].quantity += items[currentIndex].quantity;
        items.splice(currentIndex, 1);
        pulseRowIndex = collisionIndex > currentIndex ? collisionIndex - 1 : collisionIndex;
      } else {
        items[currentIndex] = { ...items[currentIndex], ...updatedFields };
        pulseRowIndex = currentIndex;
      }
      writeCart(items);
    }

    // Merge case: fade this row out via the same .is-removing transition
    // Remove already uses (already respects reduced-motion) before it's
    // spliced, then pulse the surviving row. In-place update: no fade
    // needed, just commit and let the price pulse fire on re-render.
    if (collides && !prefersReducedMotion) {
      row.classList.add("is-removing");
      setTimeout(apply, REMOVE_TRANSITION_MS);
    } else {
      apply();
    }
  }

  function closeOtherPanels(itemsEl, exceptKey) {
    if (!openSizePanelKey || openSizePanelKey === exceptKey) return;
    const openRow = findRowByKey(itemsEl, openSizePanelKey);
    if (openRow) collapsePanel(openRow.querySelector("[data-cart-size-panel]"));
  }

  // ---------- Task 4: cross-sell surface ----------
  // Cached per drawer-open session: null = not fetched yet (or a fetch is
  // still in flight), an array = fetched, already filtered down to the
  // (up to 4) usable cards to show — possibly empty. Reset in
  // resetCrossSell() every openDrawer() call, so a fresh fetch happens
  // once per open, never re-triggered by a same-session re-render
  // (quantity change, size swap).
  let crossSellCards = null;
  // Whether crossSellCards has already been painted into the DOM for
  // this open session — renderCrossSell() is called from every
  // renderDrawerContents() pass (both the empty-cart and normal
  // branches), but the actual card markup/entrance animation should only
  // happen once, not rebuilt on every unrelated re-render.
  let crossSellRendered = false;
  // Bumped on every openDrawer() call so a still-in-flight fetch from a
  // *previous* open session (drawer closed and reopened quickly) can't
  // land its result into the current session.
  let crossSellToken = 0;

  function resetCrossSell() {
    crossSellCards = null;
    crossSellRendered = false;
    crossSellToken += 1;
  }

  // Same one-shot entrance as ai-search.js's revealCard(): classes added
  // then removed again once settled, so the transition doesn't linger
  // and fight .product-card:hover's own transform transition afterward.
  function revealCrossSellCard(card, delayMs) {
    card.classList.add("is-entering");
    void card.offsetWidth;
    setTimeout(() => {
      card.classList.add("is-visible");
      setTimeout(() => {
        card.classList.remove("is-entering", "is-visible");
      }, 320);
    }, delayMs);
  }

  // Sources up to 4 addable, not-already-in-cart product cards via
  // Shopify's Section Rendering API — reusing the product page's own
  // "You might also like" (same-category) logic when the cart has a real
  // product to seed from, or the homepage's "Fresh off the cutter"
  // section (always populated) otherwise. See
  // docs/frontend-group-a-spec.md Task 4 for the full sourcing rules.
  function fetchCrossSell() {
    const token = crossSellToken;
    const items = readCart();
    // Most recently added product-type item (last in the array —
    // addProduct() always .push()es) — previews are never eligible seeds,
    // they have no product page URL.
    const seed = [...items].reverse().find((it) => it.type === "product");
    const sourceUrl = seed ? `${seed.productUrl}?section_id=main-product` : `/?section_id=hot-right-now`;

    fetch(sourceUrl)
      .then((response) => {
        if (!response.ok) throw new Error("Cross-sell fetch failed");
        return response.text();
      })
      .then((html) => {
        if (token !== crossSellToken) return; // drawer's been reopened since
        const doc = new DOMParser().parseFromString(html, "text/html");
        let cardEls;
        if (seed) {
          cardEls = Array.from(doc.querySelectorAll(".product-recommendations .product-card"));
        } else {
          // "Fresh off the cutter" renders its card set twice back-to-back
          // for the marquee's seamless loop (see hot-right-now.liquid's
          // comment on this) — dedupe by product URL first.
          const seenUrls = new Set();
          cardEls = [];
          doc.querySelectorAll(".hot-right-now__track .product-card").forEach((card) => {
            const link = card.querySelector("a[href]");
            const url = link ? link.getAttribute("href") : null;
            if (!url || seenUrls.has(url)) return;
            seenUrls.add(url);
            cardEls.push(card);
          });
        }

        const cartUrls = new Set(items.map((it) => it.productUrl).filter(Boolean));
        const usable = [];
        for (const card of cardEls) {
          if (usable.length >= 4) break;
          // main-product.liquid/hot-right-now.liquid already omit this
          // button for a sold-out variant — skipping cards without it
          // guarantees every suggestion is actually addable.
          const addButton = card.querySelector("[data-piecework-add-product]");
          if (!addButton) continue;
          const url = addButton.dataset.url;
          if (url && cartUrls.has(url)) continue; // already in the cart
          usable.push(card);
        }

        crossSellCards = usable;
        renderCrossSell();
      })
      .catch(() => {
        // Network error, or the seed product was deleted/unpublished
        // since being added — this is a supplementary surface, not a
        // primary action, so fail silently (hide) rather than showing an
        // error, unlike Task 2's size-swap fetch (a blocking action the
        // shopper explicitly initiated).
        if (token !== crossSellToken) return;
        crossSellCards = [];
        renderCrossSell();
      });
  }

  // Drops any already-rendered cross-sell card whose product is now in the
  // cart — e.g. the shopper clicked that card's own "Add to cart" button.
  // Re-run on every render (not just once per fetch) so a same-session add
  // is reflected immediately instead of leaving a redundant suggestion
  // sitting in "You might also like".
  function pruneAddedCrossSellCards(track, block) {
    const cartUrls = new Set(readCart().map((it) => it.productUrl).filter(Boolean));
    track.querySelectorAll("[data-piecework-add-product]").forEach((addButton) => {
      const url = addButton.dataset.url;
      if (!url || !cartUrls.has(url)) return;
      const card = addButton.closest(".product-card") || addButton;
      card.remove();
    });
    block.hidden = track.children.length === 0;
  }

  // Called from both branches of renderDrawerContents() (empty-cart and
  // normal) — idempotent, so calling it on every render is harmless: it
  // only actually paints cards into the DOM once per open session
  // (crossSellRendered guards re-animating on an unrelated re-render), but
  // still re-checks the cart on every call to prune cards added meanwhile.
  function renderCrossSell() {
    if (!drawer) return;
    const block = drawer.querySelector("[data-cart-cross-sell]");
    const track = drawer.querySelector("[data-cart-cross-sell-track]");
    if (!block || !track) return;

    if (crossSellCards === null) return; // still loading — stays hidden
    if (crossSellCards.length === 0) {
      block.hidden = true;
      return;
    }
    if (crossSellRendered) {
      block.hidden = false;
      pruneAddedCrossSellCards(track, block);
      return;
    }

    const prefersReducedMotion = window.matchMedia("(prefers-reduced-motion: reduce)").matches;
    track.innerHTML = "";
    crossSellCards.forEach((card, index) => {
      const clone = card.cloneNode(true);
      // Cards are cloned verbatim from main-product.liquid's "You might
      // also like" (or hot-right-now.liquid's marquee) markup, both of
      // which opt into the sitewide scroll-reveal system
      // (.scroll-reveal-item, opacity:0 by default, only revealed by
      // scroll-reveal.js's IntersectionObserver). That observer never
      // sees these clones — they're injected into the cart drawer, not
      // scrolled into view on the page — so without stripping this class
      // the card is stuck permanently invisible once this function's own
      // .is-entering/.is-visible pair is removed after settling. This
      // track has its own entrance animation (below); the leftover
      // scroll-reveal class only fights it.
      clone.classList.remove("scroll-reveal-item");
      clone.removeAttribute("data-scroll-reveal-item");
      track.appendChild(clone);
      if (!prefersReducedMotion) revealCrossSellCard(clone, index * 40);
    });
    block.hidden = false;
    crossSellRendered = true;
  }

  // ---------- Cart item preview ----------
  // Opened by clicking a line item's image/title — reopens the EXACT same
  // modal the item would open from anywhere else on the site, not a
  // separate cart-only copy: ai-search.js's own preview modal for a
  // preview-type item (full size-pill/Add to cart/Buy behavior), or
  // hot-right-now-quick-view.js's own quick-view for a real product (full
  // size-pill/Add to cart behavior). Both scripts are loaded sitewide
  // (theme.liquid) and expose `window.PieceworkPreviewModal`/
  // `window.PieceworkQuickView` for exactly this reuse.
  //
  // A real product's cart item only carries the one variant/size it was
  // added at — reopening the full quick-view (with every size) needs the
  // product's full variant list, fetched fresh each open (not reusing
  // productVariantsCache above: that cache stores plain REST variants for
  // the inline size-swap panel, priced as dollar strings, whereas
  // PieceworkQuickView expects the same shape its own card triggers give it
  // — prices in cents, matching Shopify's Liquid `product | json` — hence
  // the `price * 100` conversion below).
  function openCartItemPreview(item) {
    if (item.type === "preview") {
      if (!window.PieceworkPreviewModal) return;
      window.PieceworkPreviewModal.open({
        preview: {
          id: item.previewId,
          title: item.baseTitle || item.title,
          description: item.description,
          thumbnailUrl: item.thumbnailUrl,
          fullImageUrl: item.fullImageUrl,
          styleModifier: item.styleModifier,
          searchPhrase: item.searchPhrase,
        },
        sizes: item.sizes || [],
        backendUrl: window.PieceworkBackendUrl,
        orientation: item.orientation,
      });
      return;
    }

    if (!window.PieceworkQuickView || !item.productUrl) return;
    fetch(`${item.productUrl}.json`)
      .then((response) => {
        if (!response.ok) throw new Error("Couldn't load product");
        return response.json();
      })
      .then((data) => {
        const variants = (data.product && data.product.variants) || [];
        // REST's `tags` comes back as one comma-separated string (unlike
        // the Admin API's array) — split rather than a raw .includes()
        // substring check, so e.g. a hypothetical "orientation:horizontal-ish"
        // tag couldn't false-match "orientation:horizontal".
        const tags = ((data.product && data.product.tags) || "").split(",").map((t) => t.trim());
        const orientation = tags.includes("orientation:horizontal")
          ? "horizontal"
          : tags.includes("orientation:vertical")
          ? "vertical"
          : "";
        window.PieceworkQuickView.open(
          { title: item.title, image: (data.product && data.product.image && data.product.image.src) || item.imageUrl, url: item.productUrl },
          item.imageUrl,
          {
            description: data.product && data.product.body_html,
            variants: variants.map((v) => ({
              id: v.id,
              option1: v.option1,
              price: Math.round(Number(v.price) * 100),
              available: v.available,
            })),
          },
          orientation
        );
      })
      .catch(() => {
        // Network error/deleted product — the click just does nothing,
        // same fail-quiet treatment as the cross-sell surface's own fetch.
      });
  }

  function buildDrawer() {
    const overlay = document.createElement("div");
    overlay.className = "piecework-cart-overlay";
    overlay.innerHTML = `
      <div class="piecework-cart-drawer" role="dialog" aria-modal="true">
        <div class="piecework-cart-drawer__header">
          <h3>${strings.title || "Your cart"}</h3>
          <button type="button" class="piecework-cart-drawer__close" data-cart-close aria-label="${modalStrings.close || "Close"}">&times;</button>
        </div>
        <div class="piecework-cart-drawer__items" data-cart-items></div>
        <div class="piecework-cart-drawer__cross-sell" data-cart-cross-sell hidden>
          <div class="piecework-cart-drawer__cross-sell-heading">${strings.crossSellHeading || "You might also like"}</div>
          <div class="piecework-cart-drawer__cross-sell-track" data-cart-cross-sell-track></div>
        </div>
        <div class="piecework-cart-drawer__footer" data-cart-footer>
          <div class="piecework-cart-drawer__subtotal">
            <span>${strings.subtotal || "Subtotal"}</span>
            <span data-cart-subtotal></span>
          </div>
          <div class="piecework-cart-drawer__subtotal piecework-cart-drawer__shipping-row">
            <span>${strings.shipping || "Shipping"}</span>
            <span data-cart-shipping></span>
          </div>
          <p class="piecework-cart-drawer__shipping-note" data-cart-shipping-note></p>
          <div class="piecework-cart-drawer__status" data-cart-status hidden></div>
          <button type="button" class="btn btn-primary piecework-cart-drawer__checkout" data-cart-checkout>${strings.checkout || "Checkout"}</button>
        </div>
      </div>
    `;
    document.body.appendChild(overlay);

    overlay.addEventListener("click", (event) => {
      if (event.target === overlay) closeDrawer();
    });
    overlay.querySelector("[data-cart-close]").addEventListener("click", closeDrawer);
    overlay.querySelector("[data-cart-checkout]").addEventListener("click", handleCheckout);
    document.addEventListener("keydown", (event) => {
      if (event.key === "Escape") closeDrawer();
    });

    return overlay;
  }

  function renderDrawerContents(items) {
    items = items || readCart();
    const itemsEl = drawer.querySelector("[data-cart-items]");
    const footerEl = drawer.querySelector("[data-cart-footer]");

    if (items.length === 0) {
      const homeUrl = document.querySelector(".site-header")?.dataset.homeUrl || "/";
      itemsEl.innerHTML = `
        <div class="piecework-cart-drawer__empty">
          <p>${strings.empty || "Your cart is empty."}</p>
          <a href="${homeUrl}#ai-search" class="btn btn-primary">${strings.continueShopping || "Continue shopping"}</a>
        </div>
      `;
      footerEl.hidden = true;
      // Shown in both the empty and non-empty cart states — arguably more
      // valuable here, since the empty state otherwise offers nothing but
      // a "Continue shopping" link. No real cart item to seed a category
      // from, so this always falls into the "Fresh off the cutter"
      // fallback sourcing rule.
      renderCrossSell();
      return;
    }
    footerEl.hidden = false;

    const prefersReducedMotion = window.matchMedia("(prefers-reduced-motion: reduce)").matches;

    itemsEl.innerHTML = "";
    items.forEach((item, index) => {
      const row = document.createElement("div");
      row.className = "piecework-cart-item";
      const image = item.type === "preview" ? item.thumbnailUrl : item.imageUrl;
      const key = itemKey(item);
      row.dataset.cartItemKey = key;

      // Size-swap trigger (Task 2): rendered only when this row's piece
      // count is known — always true for preview items, true for product
      // items only once the Shared Prerequisite data model change has
      // shipped and the item was added afterward. A product item added
      // before that ships (or missing pieces for any other reason) shows
      // nothing here, same as today's blank subtitle for products — no
      // broken/dead trigger.
      const sizeTriggerHtml =
        item.pieces != null
          ? `
            <button type="button" class="piecework-cart-item__size-trigger" data-cart-size-trigger>
              ${item.pieces} pc <span class="piecework-cart-item__size-trigger-icon" aria-hidden="true">✎</span>
            </button>
            <div class="piecework-cart-item__size-panel" data-cart-size-panel hidden>
              <div class="piecework-cart-item__size-panel-inner" data-cart-size-panel-inner></div>
            </div>
          `
          : "";

      // Quantity stepper (Task 3): +/- buttons flank a typable number
      // input (owner feedback 2026-07-14 — the spec's original
      // non-editable <span> made typing an exact quantity impossible).
      // Floor-and-disable at both ends (min 1 / max 99, owner-confirmed),
      // Remove remains the one deliberate way to delete a line item.
      row.innerHTML = `
        <img src="${escapeHtml(image || "")}" alt="" class="piecework-cart-item__image" data-cart-item-preview-trigger>
        <div class="piecework-cart-item__body">
          <div class="piecework-cart-item__title" data-cart-item-preview-trigger>${escapeHtml(item.title)}</div>
          ${sizeTriggerHtml}
          <div class="piecework-cart-item__row">
            <div class="piecework-cart-item__stepper" data-cart-stepper>
              <button type="button" class="piecework-cart-item__stepper-btn" data-cart-qty-decrement aria-label="Decrease quantity"${
                item.quantity <= 1 ? " disabled" : ""
              }>&minus;</button>
              <input type="number" class="piecework-cart-item__stepper-value" data-cart-qty-value aria-label="Quantity" min="1" max="99" step="1" value="${item.quantity}">
              <button type="button" class="piecework-cart-item__stepper-btn" data-cart-qty-increment aria-label="Increase quantity"${
                item.quantity >= 99 ? " disabled" : ""
              }>+</button>
            </div>
            <span class="piecework-cart-item__price">${formatMoney(item.price * item.quantity)}</span>
          </div>
          <button type="button" class="piecework-cart-item__remove" data-cart-remove>${strings.remove || "Remove"}</button>
        </div>
      `;
      if (index === pulseRowIndex) {
        pulse(row.querySelector(".piecework-cart-item__price"), "is-updated");
        pulseRowIndex = null;
      }

      row.querySelectorAll("[data-cart-item-preview-trigger]").forEach((el) => {
        el.addEventListener("click", () => openCartItemPreview(item));
      });

      row.querySelector("[data-cart-qty-decrement]").addEventListener("click", () => {
        pulseRowIndex = index;
        changeQuantity(key, item.quantity - 1);
      });
      row.querySelector("[data-cart-qty-increment]").addEventListener("click", () => {
        pulseRowIndex = index;
        changeQuantity(key, item.quantity + 1);
      });

      // Typed entry: commit on blur or Enter (not on every keystroke —
      // committing mid-type would fight the user re-rendering the field
      // out from under their own cursor). Invalid/empty input falls back
      // to the item's current quantity rather than committing garbage.
      const qtyInput = row.querySelector("[data-cart-qty-value]");
      function commitTypedQty() {
        const parsed = parseInt(qtyInput.value, 10);
        const next = Number.isFinite(parsed) ? parsed : item.quantity;
        if (next === item.quantity) {
          qtyInput.value = item.quantity; // normalize e.g. "" or "07"
          return;
        }
        pulseRowIndex = index;
        changeQuantity(key, next);
      }
      qtyInput.addEventListener("blur", commitTypedQty);
      qtyInput.addEventListener("keydown", (event) => {
        if (event.key === "Enter") {
          event.preventDefault();
          qtyInput.blur();
        }
      });

      const sizeTrigger = row.querySelector("[data-cart-size-trigger]");
      if (sizeTrigger) {
        const panel = row.querySelector("[data-cart-size-panel]");
        const panelInner = row.querySelector("[data-cart-size-panel-inner]");
        sizeTrigger.addEventListener("click", () => {
          if (openSizePanelKey === key) {
            collapsePanel(panel);
            openSizePanelKey = null;
            return;
          }
          // Only one panel open at a time — collapse whichever other
          // row's panel is currently open before expanding this one.
          closeOtherPanels(itemsEl, key);
          openSizePanelKey = key;
          populateSizePanel(item, panelInner, key, row);
        });

        // A re-render happened (e.g. a quantity change on another row)
        // while this row's panel was open — restore it instantly, no
        // re-animate, so the shopper doesn't lose their place.
        if (openSizePanelKey === key) {
          reopenPanelAfterRender(item, key, row);
        }
      }

      // Keyed rather than index-based: the fade-out below delays the actual
      // removal by REMOVE_TRANSITION_MS, and a second Remove click on a
      // different row within that window would otherwise capture a now-stale
      // numeric index once the first click's re-render shifts everything
      // down by one.
      row.querySelector("[data-cart-remove]").addEventListener("click", () => {
        if (prefersReducedMotion) {
          removeByKey(key);
          return;
        }
        row.classList.add("is-removing");
        setTimeout(() => removeByKey(key), REMOVE_TRANSITION_MS);
      });
      itemsEl.appendChild(row);
    });

    const subtotal = getSubtotal(items);
    const fee = getShippingFee(items);
    drawer.querySelector("[data-cart-subtotal]").textContent = formatMoney(subtotal);
    drawer.querySelector("[data-cart-shipping]").textContent = fee === 0 ? strings.shippingFree || "Free" : formatMoney(fee);
    drawer.querySelector("[data-cart-shipping-note]").textContent = strings.shippingNote || "Taxes calculated at checkout.";

    renderCrossSell();
  }

  function openDrawer() {
    if (!drawer) drawer = buildDrawer();
    // Fetched once per drawer-open session (not re-fetched by a
    // same-session re-render triggered by a quantity change or size
    // swap) — reset here, before renderDrawerContents()'s first
    // renderCrossSell() call, and kicked off right after so the fetch is
    // already in flight while the rest of the drawer paints.
    resetCrossSell();
    renderDrawerContents();
    fetchCrossSell();
    // Force a reflow before toggling .is-open — needed for a just-built
    // drawer specifically. Without this, buildDrawer() inserting the
    // element and this function adding .is-open happen in the same tick,
    // so the browser never paints the "closed" state (translateX(100%),
    // opacity 0) as a real frame and just snaps straight to "open" —
    // the slide-in transition never plays. Every open after the first
    // works fine already, since by then the drawer's closed state has
    // actually been painted (from the previous close).
    void drawer.offsetWidth;
    drawer.classList.add("is-open");
  }

  function closeDrawer() {
    if (drawer) drawer.classList.remove("is-open");
  }

  async function handleCheckout() {
    const items = readCart();
    if (items.length === 0) return;
    // Checkout intent for the custom cart. Fired on the drawer's Checkout
    // click (the moment we hand off to the Draft Order invoice), since the
    // app pixel can't observe this custom flow. Note: if Shopify's hosted
    // checkout page also emits checkout_started, this can slightly overlap;
    // verify in Test Events and drop this call if it double-counts.
    trackPixel("InitiateCheckout", {
      content_type: "product",
      content_ids: items.map(itemKey),
      value: getSubtotal(items),
      currency: PIXEL_CURRENCY,
      num_items: getCount(items),
    });
    const backendUrl = window.PieceworkBackendUrl;
    const statusEl = drawer.querySelector("[data-cart-status]");
    const checkoutButton = drawer.querySelector("[data-cart-checkout]");

    checkoutButton.disabled = true;
    statusEl.hidden = false;
    statusEl.textContent = modalStrings.startingCheckout || "Starting checkout...";

    const payload = items.map((item) =>
      item.type === "preview"
        ? { type: "preview", previewId: item.previewId, pieces: item.pieces, quantity: item.quantity }
        : { type: "product", variantId: item.variantId, quantity: item.quantity }
    );

    try {
      const response = await fetch(`${backendUrl}/api/checkout-cart`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ items: payload }),
      });
      const data = await response.json();
      if (!response.ok || !data.checkoutUrl) {
        statusEl.textContent = data.error || modalStrings.checkoutError || "Couldn't start checkout. Please try again.";
        checkoutButton.disabled = false;
        return;
      }
      if (data.draftOrderId) {
        localStorage.setItem(PENDING_CHECKOUT_KEY, JSON.stringify({ draftOrderId: data.draftOrderId, startedAt: Date.now() }));
      }
      window.location.href = data.checkoutUrl;
    } catch (err) {
      statusEl.textContent = modalStrings.networkError || "Network error — please try again.";
      checkoutButton.disabled = false;
    }
  }

  // Runs once on every page load (see the bottom of this file). If the
  // customer actually paid the pending draft order, clears the cart for
  // real and the pending marker with it. If not — still open, or the
  // status check itself failed — leaves the cart exactly as it is and
  // tries again on the next page load, up to PENDING_CHECKOUT_MAX_AGE_MS.
  async function checkPendingCheckout() {
    const raw = localStorage.getItem(PENDING_CHECKOUT_KEY);
    if (!raw) return;

    let pending;
    try {
      pending = JSON.parse(raw);
    } catch (err) {
      localStorage.removeItem(PENDING_CHECKOUT_KEY);
      return;
    }

    if (!pending || !pending.draftOrderId) {
      localStorage.removeItem(PENDING_CHECKOUT_KEY);
      return;
    }

    if (Date.now() - (pending.startedAt || 0) > PENDING_CHECKOUT_MAX_AGE_MS) {
      localStorage.removeItem(PENDING_CHECKOUT_KEY);
      return;
    }

    const backendUrl = window.PieceworkBackendUrl;
    if (!backendUrl) return;

    try {
      const response = await fetch(`${backendUrl}/api/checkout-status`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ draftOrderId: pending.draftOrderId }),
      });
      if (!response.ok) return; // try again next page load
      const data = await response.json();
      if (data.completed) {
        localStorage.removeItem(STORAGE_KEY);
        localStorage.removeItem(PENDING_CHECKOUT_KEY);
        renderBadge([]);
        if (drawer) renderDrawerContents([]);
      }
    } catch (err) {
      // Network error — try again next page load, cart stays intact either way.
    }
  }

  window.PieceworkCart = {
    addPreview,
    addProduct,
    removeByKey,
    changeQuantity,
    open: openDrawer,
    close: closeDrawer,
    getItems: readCart,
    flashAdded,
  };

  renderBadge(readCart());
  checkPendingCheckout();
  document.querySelectorAll("[data-piecework-cart-toggle]").forEach((el) => {
    el.addEventListener("click", (event) => {
      event.preventDefault();
      openDrawer();
    });
  });

  // Delegated so it also covers add-to-cart buttons rendered after this
  // script runs (e.g. Section Rendering API re-renders, dynamic sections).
  document.addEventListener("click", (event) => {
    const button = event.target.closest("[data-piecework-add-product]");
    if (!button) return;
    addProduct({
      variantId: button.dataset.variantId,
      title: button.dataset.title,
      price: Number(button.dataset.price),
      imageUrl: button.dataset.image,
      productUrl: button.dataset.url,
      pieces: button.dataset.pieces ? Number(button.dataset.pieces) : null,
    });
    flashAdded(button, productStrings.added);
  });
})();
